import DebugHelper from './DebugHelper.js';
import PositionSync from './MultiplayerPlayerManager/PositionSync.js';
import PlayerRenderer from './MultiplayerPlayerManager/PlayerRenderer.js';
import TeamFollowing from './MultiplayerPlayerManager/TeamFollowing.js';

export default class MultiplayerPlayerManager {
    constructor(scene, networkManager) {
        this.scene = scene;
        this.networkManager = networkManager;
        
        this.positionSync = new PositionSync(scene, networkManager);
        this.playerRenderer = new PlayerRenderer(scene);
        this.teamFollowing = new TeamFollowing(scene, networkManager, this.positionSync, this.playerRenderer);
    }
    
    /**
     * 初始化多人玩家管理器
     */
    init() {
        if (!this.networkManager) {
            DebugHelper.warn('MultiplayerPlayerManager: 网络管理器不存在');
            return;
        }

        this.destroyNetworkSubscriptions();
        
        this._networkUnsubs = [
            this.networkManager.on('playerJoin', (playerId, playerData) => {
            // 检查玩家的场景ID是否匹配当前场景
            const currentSceneId = this.scene.scene.key;
            const playerSceneId = playerData?.sceneId;
            
            // 如果玩家有场景ID，必须匹配当前场景
            if (playerSceneId) {
                if (playerSceneId === currentSceneId) {
                    DebugHelper.log(`收到 playerJoin 消息，添加同一场景的玩家: ${playerId} (场景: ${playerSceneId})`);
                    this.addRemotePlayer(playerId, playerData);
                } else {
                    // 场景ID不匹配，但这是服务器发送的 playerJoin 消息
                    // 可能是场景切换时的时序问题（服务器已经更新了场景ID，但客户端还没有）
                    // 检查 playerData 是否包含完整信息（不仅仅是位置数据）
                    const hasFullData = playerData && Object.keys(playerData).filter(
                        key => !['x', 'y', 'direction', 'sceneId'].includes(key)
                    ).length > 0;
                    
                    if (hasFullData) {
                        // 数据完整，可能是场景切换时的时序问题，暂时添加玩家
                        DebugHelper.log(`收到 playerJoin 消息，场景ID不匹配但数据完整，添加: ${playerId} (场景: ${playerSceneId}, 当前场景: ${currentSceneId}, 等待场景同步)`);
                        this.addRemotePlayer(playerId, playerData);
                    } else {
                        DebugHelper.log(`收到 playerJoin 消息，但玩家在不同场景且数据不完整，跳过: ${playerId} (场景: ${playerSceneId}, 当前场景: ${currentSceneId})`);
                    }
                }
            } else {
                // 如果玩家没有场景ID，但这是 playerJoin 消息（说明服务器认为玩家应该在这个场景）
                // 可能是场景切换时的时序问题，暂时添加玩家，场景ID会在后续更新中同步
                // 检查 playerData 是否包含完整信息（不仅仅是位置数据）
                const hasFullData = playerData && Object.keys(playerData).filter(
                    key => !['x', 'y', 'direction'].includes(key)
                ).length > 0;
                
                if (hasFullData) {
                    DebugHelper.log(`收到 playerJoin 消息，玩家没有场景ID但数据完整，添加: ${playerId} (等待场景ID更新)`);
                    this.addRemotePlayer(playerId, playerData);
                } else {
                    DebugHelper.log(`收到 playerJoin 消息，但玩家没有场景ID且数据不完整，跳过: ${playerId} (等待完整数据)`);
                }
            }
            }),
            this.networkManager.on('playerLeave', (playerId) => {
                this.removeRemotePlayer(playerId);
            }),
            this.networkManager.on('playerUpdate', (playerId, playerData) => {
                this.updateRemotePlayer(playerId, playerData);
            })
        ];
        
        // 初始化已存在的玩家
        // 只添加同一场景的玩家，避免在不同场景中看到其他场景的玩家
        const currentSceneId = this.scene.scene.key;
        const existingPlayers = this.networkManager.getPlayers();
        DebugHelper.log('初始化已存在的玩家:', existingPlayers.length, '当前场景:', currentSceneId);
        
        existingPlayers.forEach(player => {
            if (player.id !== this.networkManager.playerId) {
                // 检查玩家的场景ID是否匹配当前场景
                const playerSceneId = player.sceneId || player.playerData?.sceneId;
                
                // 如果玩家有场景ID，必须匹配当前场景
                // 如果玩家没有场景ID，可能是还在初始化中，暂时不添加（等待 playerJoin 消息）
                if (playerSceneId) {
                    if (playerSceneId === currentSceneId) {
                        DebugHelper.log(`添加同一场景的玩家: ${player.id} (场景: ${playerSceneId})`);
                        this.addRemotePlayer(player.id, player);
                    } else {
                        DebugHelper.log(`跳过不同场景的玩家: ${player.id} (场景: ${playerSceneId}, 当前场景: ${currentSceneId})`);
                    }
                } else {
                    // 如果玩家没有场景ID，可能是还在初始化中，暂时不添加
                    // 等待服务器发送 playerJoin 消息，或者等待玩家设置场景ID
                    DebugHelper.log(`跳过没有场景ID的玩家: ${player.id} (等待场景ID设置)`);
                }
            }
        });
    }
    
    /**
     * 添加远程玩家
     * @param {string} playerId - 玩家ID
     * @param {Object} playerData - 玩家数据
     */
    addRemotePlayer(playerId, playerData) {
        this.playerRenderer.addRemotePlayer(playerId, playerData);
    }
    
    /**
     * 移除远程玩家
     * @param {string} playerId - 玩家ID
     */
    removeRemotePlayer(playerId) {
        this.playerRenderer.removeRemotePlayer(playerId);
        this.positionSync.clearPlayerData(playerId);
    }
    
    /**
     * 更新远程玩家状态
     * @param {string} playerId - 玩家ID
     * @param {Object} playerData - 玩家数据
     */
    updateRemotePlayer(playerId, playerData) {
        const sprite = this.playerRenderer.getPlayerSprite(playerId);
        const nameTag = this.playerRenderer.playerNameTags.get(playerId);
        
        if (!sprite) {
            // 如果玩家不存在，尝试添加
            // 注意：playerData 可能只是位置更新数据（{x, y, direction}），
            // 需要从 networkManager 获取完整的玩家数据
            const fullPlayerData = this.networkManager.getPlayer(playerId);
            
            // 如果 networkManager 中没有完整数据，说明这是位置更新但玩家还未创建
            // 这种情况下不应该创建玩家，应该等待 playerJoin 消息
            if (!fullPlayerData || Object.keys(fullPlayerData).filter(
                key => !['x', 'y', 'direction'].includes(key)
            ).length === 0) {
                DebugHelper.warn(`updateRemotePlayer: 玩家 ${playerId} 不存在，但 playerData 只包含位置信息，跳过创建。等待 playerJoin 消息。playerData=`, playerData);
                return;
            }
            
            DebugHelper.log(`updateRemotePlayer: 玩家不存在，使用完整数据添加: playerId=${playerId}, fullPlayerData=`, fullPlayerData);
            this.addRemotePlayer(playerId, fullPlayerData);
            return;
        }
        
        // 检查这个远程玩家是否是队长（如果本地玩家是队员）
        const isRemoteLeader = this.teamFollowing.teamLeaderId && this.teamFollowing.teamLeaderId === playerId;
        
        // 如果本地玩家是队员，远程玩家是队长，需要跟随队长
        // 先更新队长精灵的位置和方向，然后使用updateFollowerPositionFromData计算跟随位置
        if (isRemoteLeader && !this.teamFollowing.isLocalPlayerLeader && this.teamFollowing.localPlayerSprite) {
            // 先更新队长精灵的位置和方向（这样精灵的data中就有最新的方向信息）
            if (playerData.x !== undefined && playerData.y !== undefined) {
                sprite.x = playerData.x;
                sprite.y = playerData.y;
            }
            if (playerData.direction) {
                sprite.setData('direction', playerData.direction);
            }
            
            // 使用updateFollowerPositionFromData方法计算跟随位置（这会正确处理方向信息）
            this.teamFollowing.updateFollowerPositionFromData({
                x: playerData.x,
                y: playerData.y,
                direction: playerData.direction
            });
        }
        
        // 更新远程玩家位置
        // 如果本地玩家是队长，远程玩家是队员，完全忽略服务器位置（由updateTeamMemberPositions控制）
        if (this.teamFollowing.isLocalPlayerLeader && this.teamFollowing.localPlayerSprite) {
            const offset = this.teamFollowing.memberOffsets.get(playerId);
            if (offset) {
                // 队员位置由updateTeamMemberPositions控制，这里不更新
                return; // 直接返回，不更新位置
            }
        }
        
        // 其他情况使用服务器位置
        const result = this.positionSync.updateRemotePlayerPosition(playerId, playerData, sprite, nameTag);
        
        // 更新方向和动画
        if (playerData.direction) {
            sprite.setData('direction', playerData.direction);
            
            // 更新动画（只在移动状态改变时更新）
            const lastIsMoving = this.positionSync.getMovingState(playerId);
            const lastDirection = this.positionSync.getDirection(playerId);
            if (result.isMoving !== lastIsMoving || playerData.direction !== lastDirection) {
                this.playerRenderer.updateRemotePlayerAnimation(sprite, playerId, playerData.direction, result.isMoving);
                this.positionSync.setMovingState(playerId, result.isMoving);
            }
        }
        
        // 更新名称
        if (playerData.playerName && nameTag) {
            this.playerRenderer.updateNameTag(playerId, playerData.playerName);
        }
    }
    
    /**
     * 更新本地玩家位置（发送到服务器）
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {string} direction - 方向
     */
    updateLocalPlayerPosition(x, y, direction) {
        // 如果本地玩家是队长，记录位置历史用于蛇形跟随
        if (this.teamFollowing.isLocalPlayerLeader) {
            this.teamFollowing.recordLeaderPosition(x, y, direction);
        }
        
        this.positionSync.updateLocalPlayerPosition(
            x, 
            y, 
            direction,
            this.teamFollowing.isLocalPlayerLeader,
            this.teamFollowing.teamLeaderId
        );
    }
    
    /**
     * 更新跟随位置（当本地玩家是队员时调用）
     * @param {Phaser.GameObjects.Sprite} localSprite - 本地玩家精灵
     */
    updateFollowerPosition(localSprite) {
        this.teamFollowing.updateFollowerPosition(localSprite);
    }
    
    /**
     * 更新队员位置（当本地玩家是队长时调用）
     */
    updateTeamMemberPositions() {
        // 尝试从场景获取本地玩家精灵
        let localPlayerSprite = this.teamFollowing.localPlayerSprite;
        if (!localPlayerSprite && this.scene) {
            if (this.scene.playerSprite) {
                localPlayerSprite = this.scene.playerSprite;
            } else if (this.scene.worldPlayer && this.scene.worldPlayer.playerSprite) {
                localPlayerSprite = this.scene.worldPlayer.playerSprite;
            } else if (this.scene.worldPlayer) {
                localPlayerSprite = this.scene.worldPlayer.getCurrentControlledSprite();
            }
            
            if (localPlayerSprite) {
                this.teamFollowing.setLocalPlayer(localPlayerSprite, this.scene.vehicleSprite);
            }
        }
        
        if (!localPlayerSprite) {
            return;
        }
        
        // 获取队伍成员列表
        let teamMemberIds = [];
        if (this.scene && this.scene.teamManager) {
            const teamMembers = this.scene.teamManager.getTeamMembers();
            teamMemberIds = teamMembers.map(member => member.playerId);
        }
        
        this.teamFollowing.updateTeamMemberPositions(localPlayerSprite, teamMemberIds);
    }
    
    /**
     * 设置本地玩家引用（从场景调用）
     * @param {Phaser.GameObjects.Sprite} localPlayerSprite - 本地玩家精灵
     * @param {Phaser.GameObjects.Sprite} localVehicleSprite - 本地战车精灵
     */
    setLocalPlayer(localPlayerSprite, localVehicleSprite) {
        this.teamFollowing.setLocalPlayer(localPlayerSprite, localVehicleSprite);
    }
    
    /**
     * 设置队伍队长（用于跟随功能）
     * @param {string} leaderId - 队长ID
     * @param {Array} teamMembers - 队伍成员列表
     */
    setTeamLeader(leaderId, teamMembers = []) {
        this.teamFollowing.setTeamLeader(leaderId, teamMembers);
    }
    
    /**
     * 设置本地玩家为队长
     * @param {Array} teamMembers - 队伍成员列表
     */
    setLocalPlayerAsLeader(teamMembers = []) {
        this.teamFollowing.setLocalPlayerAsLeader(teamMembers);
    }
    
    /**
     * 清除队伍状态
     */
    clearTeam() {
        this.teamFollowing.clearTeam();
    }
    
    /**
     * 获取所有远程玩家
     * @returns {Array} 玩家列表
     */
    getRemotePlayers() {
        return this.playerRenderer.getRemotePlayers();
    }
    
    /**
     * 每帧更新远程玩家插值
     * @param {number} delta
     */
    tick(delta) {
        this.positionSync.tickRemoteInterpolation(this.playerRenderer, delta);
    }

    destroyNetworkSubscriptions() {
        if (this._networkUnsubs) {
            this._networkUnsubs.forEach((unsub) => {
                if (typeof unsub === 'function') {
                    unsub();
                }
            });
            this._networkUnsubs = null;
        }
    }

    /**
     * 清理所有远程玩家
     */
    destroy() {
        this.destroyNetworkSubscriptions();
        this.playerRenderer.destroy();
    }
}
